import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {BaseRepository} from './base/base.repository';
import {PostPayloadInterface} from '../models/post-payload.interface';
import {Observable} from 'rxjs';
import {GetPayloadInterface} from '../models/get-payload.interface';
import {Utilities} from "../utils/utilities";

@Injectable({
    providedIn: 'root'
})
export class ReservationRepository extends BaseRepository {
    private readonly staffPath: string = null;

    constructor(public httpClient: HttpClient) {
        super(httpClient);
        this.path = 'dashboard/admin/reservations';
        this.staffPath = 'dashboard/staff/reservations';
    }


    getAllByFilterForStaff(filter: GetPayloadInterface): Observable<any> {
        const path = this.staffPath + '/new';
        const options = {params: new HttpParams().append('filter', JSON.stringify(filter))};
        return super.get(path, options);
    }
    getAllByFilterForStaffTest(restaurantHistoryId, from): Observable<any> {
        const path = this.staffPath + '/test/'+restaurantHistoryId;
        const options = {params: new HttpParams().append('from', from)};
        return super.get(path, options);
    }

    getStatisticByRestaurantHistory(id:number, startDate:string, endDate:string){
        const path = 'dashboard/staff/restaurant-histories/reservation-statistics/' + id + '';
        const parameters = new HttpParams()
            .append('from', startDate)
            .append('to', endDate);
        const options = {params: parameters};
        return super.get(path, options);
    }

    decrementRestaurantReservationPersonCount(payload: PostPayloadInterface){
        const path = this.staffPath + '/' + payload.attribute.reservationNumber + '/decrement-person-count';
        return super.patch(path, payload);
    }

    incrementRestaurantReservationPersonCount(payload: PostPayloadInterface){
        const path = this.staffPath + '/' + payload.attribute.reservationNumber + '/increment-person-count';
        return super.patch(path, payload);
    }


    reAssocBillingData(billingDataId: number, id: number, pathPostFix: string) {
        const path = this.staffPath + '/' + id + '/re-assoc/' + pathPostFix + '/' + billingDataId;
        return super.patch(path);
    }

    accept(id:number){
        const path = this.staffPath + '/' + id + '/accept';
        return super.patch(path);
    }
    success(id:number){
        const path = this.staffPath + '/' + id + '/success';
        return super.patch(path);
    }

    pending(id) {
        const path = this.staffPath + '/' + id + '/pending';
        return super.patch(path);
    }

    notArrived(id:number){
        const path = this.staffPath + '/' + id + '/not-arrived';
        return super.patch(path);
    }

    declineReservation(reservationId: number, reason:string) {
        const path = this.staffPath + '/' + reservationId.toString() + '/decline';
        return super.patch(path, {id:0, type:'decline', attribute:reason});
    }

    declineReservationAndRegenerateVoucher(reservationId: number, reason:string) {
        const path = this.staffPath + '/' + reservationId.toString() + '/decline-with-voucher';
        return super.patch(path,{id:0, type:'decline', attribute:reason});
    }

    changeStatus(id: number, status: string) {
        const path = this.staffPath + '/change-status/' + status + '/' + id;
        return super.get(path);
    }

    getFreeCapacity(restaurantHistoryId: number) {
        const path = 'menu/api/free-capacity-for-year/' + restaurantHistoryId;
        return super.get(path);
    }

    getDefaultSpaceInMin() {
        const path = this.staffPath + '/default-min-space';
        return super.get(path);
    }

    getSpaceForDate(restaurantHistoryId, reservationDate) {
        const path = 'dashboard/staff/restaurant-histories/' + restaurantHistoryId + '/free-space';
        const parameters = new HttpParams().append('reservationDate', reservationDate);
        const options = {params: parameters};
        return super.get(path, options);
    }
    getSpaceForAYear(restaurantHistoryId) {
        const path = 'dashboard/staff/restaurant-histories/' + restaurantHistoryId + '/free-space-for-a-year';
        return super.get(path);
    }

    getCapacityForDate(restaurantHistoryId, reservationDate) {
        const path = 'dashboard/staff/restaurant-histories/' + restaurantHistoryId + '/free-capacity';
        const parameters = new HttpParams().append('reservationDate', reservationDate);
        const options = {params: parameters};
        return super.get(path, options);
    }

    changeDeliveryDate(id: number, newDate: string, newTime: string, duration: number) {
        const path = this.staffPath + '/change-delivery-date/' + id;
        let parameters = new HttpParams().append('newDate', newDate);
        parameters = parameters.append('newTime', newTime);
        parameters = parameters.append('duration', duration.toString());
        const options = {params: parameters};
        return super.patch(path, null, options);
    }

    save(payload: PostPayloadInterface): Observable<any> {
        const path = this.staffPath + '/base';
        return super.post(path, payload);
    }

    finalize(reservationNumber: string, skipSpotValidation:boolean): Observable<any> {
        const path = this.staffPath + '/' + reservationNumber + '/finalize';
        const parameters = new HttpParams().append('skipSpotValidation', skipSpotValidation.toString());
        const options = {params: parameters};
        return super.patch(path, null, options);
    }

    upgradeToExtraByVoucher(payload: PostPayloadInterface) {
        const path = this.staffPath + '/upgrade/extra/by-voucher';
        return super.patch(path, payload);
    }
    upgradeToExtraByProduct(payload: PostPayloadInterface) {
        const path = this.staffPath + '/upgrade/extra';
        return super.patch(path, payload);
    }
    upgradeToExtraByProductWithDelete(payload: PostPayloadInterface) {
        const path = this.staffPath + '/upgrade/extra/with-delete';
        return super.patch(path, payload);
    }
    downgradeExtra(payload: PostPayloadInterface) {
        const path = this.staffPath + '/downgrade/extra';
        return super.patch(path, payload);
    }

    downgradeExtraWithoutDelete(payload: PostPayloadInterface) {
        const path = this.staffPath + '/downgrade/extra/without-delete';
        return super.patch(path, payload);
    }

    upgradeToDeliveryByVoucher(payload: PostPayloadInterface) {
        const path = this.staffPath + '/upgrade/delivery/by-voucher';
        return super.patch(path, payload);
    }

    updateBase(payload:PostPayloadInterface){
        const path = this.staffPath + '/base/'+payload.type;
        return super.put(path, payload);
    }

    toggleExclusive(id: number) {
        const path = this.staffPath +'/'+id+'/exclusive/toggle';
        return super.patch(path);
    }

    getSpaceForAYearWithoutActualReservation(reservationId: number, restaurantHistoryId: number) {
        const path = 'dashboard/staff/restaurant-histories/' + restaurantHistoryId + '/free-space-for-a-year-without/'+reservationId;
        return super.get(path);
    }

    reSendEmail(id: number) {
        const path = this.staffPath +'/'+id+'/resend-email';
        return super.patch(path);
    }

    updateReservationLanguage(id, reservationLanguage) {
        const path = this.staffPath +'/'+id+'/change-language/'+reservationLanguage;
        return super.patch(path);
    }

    getSummarize(type: string, restaurantHistoryId: number, from:Date, to:Date) {
        const path = 'dashboard/staff/reservations/summarize/'+type+'/'+restaurantHistoryId;
        let parameters = new HttpParams().append('from', Utilities.dateToBackendString(Utilities.addDays(from, -1)));
        parameters = parameters.append('to', Utilities.dateToBackendString(Utilities.addDays(to, 1)));
        const options = {params: parameters};
        return super.get(path, options);
    }

    tookVoucher(id: number) {
        const path = this.staffPath +'/'+id+'/took-voucher';
        return super.patch(path);
    }
}
